import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Avatar,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  IconButton,
  Box,
  Text,
  Flex,
  Container,
  HStack,
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
  Icon,
  Badge,
  Divider,
  List,
  ListItem,
} from "@chakra-ui/react";
import { SearchIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  FaUser,
  FaHeart,
  FaSignOutAlt,
  FaPlus,
  FaShieldAlt,
  FaSearch,
} from "react-icons/fa";
import { getAvatarProps } from "../../utils/imageUtils";
import axios from "axios";
import Modallogin from "../auth/Modallogin";
import { useLang } from "../../contexts/LangContext";
import { FiSearch } from "react-icons/fi";
import { useChatNotif } from "../../contexts/ChatNotifContext";
import { useFavorites } from "../../contexts/FavoritesContext";
import { AUTH_CHANGED_EVENT, readIdentity, clearIdentity } from "../../utils/authEvents";

const RED = "#a21b1b";
const RED_DARK = "#881616";

// ─── Debounce hook ────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── SearchWithAutocomplete ───────────────────────────────────────────────────
function SearchWithAutocomplete({ onSearch, placeholder, inputProps = {} }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const debouncedQ = useDebounce(query, 220);
  const { t } = useLang();

  const showDropdown = focused && query.trim().length >= 2;

  // Fetch suggestions when debounced query changes
  useEffect(() => {
    let cancelled = false;
    if (debouncedQ.trim().length < 2) { setSuggestions([]); return; }
    axios
      .get(`${backendUrl}/suggestions?q=${encodeURIComponent(debouncedQ)}`)
      .then((res) => { if (!cancelled) { setSuggestions(res.data || []); setActiveIdx(-1); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [debouncedQ, backendUrl]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSearch = useCallback(
    (q) => {
      const trimmed = (q || query).trim();
      if (trimmed) { setFocused(false); setSuggestions([]); onSearch(trimmed); }
    },
    [query, onSearch]
  );

  // activeIdx: -1 = "Rechercher [terme]" row, 0..n-1 = suggestions
  const handleKeyDown = (e) => {
    if (!showDropdown) { if (e.key === "Enter") doSearch(query); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) { setQuery(suggestions[activeIdx]); doSearch(suggestions[activeIdx]); }
      else doSearch(query);
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  return (
    <Box ref={wrapperRef} position="relative" w="100%">
      <form onSubmit={(e) => { e.preventDefault(); doSearch(query); }}>
        <InputGroup>
          <InputLeftElement h="full" pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={placeholder}
            bg="gray.50"
            border="2px solid"
            borderColor="gray.200"
            borderRadius="md"
            h="44px"
            pl={10}
            pr="44px"
            fontSize="md"
            color="gray.800"
            _placeholder={{ color: "gray.500" }}
            _hover={{ borderColor: "gray.300" }}
            _focus={{ borderColor: RED, boxShadow: "none", bg: "white" }}
            value={query}
            onChange={(e) => { setQuery(e.target.value); if (e.target.value.trim().length < 2) setSuggestions([]); }}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            {...inputProps}
          />
          {/* Bouton recherche cliquable */}
          <InputRightElement h="full" w="44px">
            <Box
              as="button"
              type="submit"
              h="32px" w="32px"
              bg={query.trim() ? RED : "gray.200"}
              borderRadius="md"
              display="flex" alignItems="center" justifyContent="center"
              transition="background 0.15s"
              _hover={{ bg: query.trim() ? RED_DARK : "gray.300" }}
              aria-label="Rechercher"
            >
              <Icon as={FiSearch} boxSize={4} color={query.trim() ? "white" : "gray.500"} />
            </Box>
          </InputRightElement>
        </InputGroup>
      </form>

      {/* Dropdown */}
      {showDropdown && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0} right={0}
          bg="white"
          borderRadius="lg"
          boxShadow="0 8px 24px rgba(0,0,0,0.12)"
          border="1px solid" borderColor="gray.100"
          zIndex={2000}
          overflow="hidden"
        >
          <List spacing={0}>
            {/* Ligne "Rechercher [terme]" toujours en tête */}
            <ListItem
              px={4} py={2.5} cursor="pointer" minH="44px"
              bg={activeIdx === -1 ? "red.50" : "white"}
              color={activeIdx === -1 ? RED : "gray.700"}
              fontWeight="600" fontSize="sm"
              _hover={{ bg: "red.50", color: RED }}
              transition="background 0.1s"
              display="flex" alignItems="center" gap={2}
              borderBottom={suggestions.length > 0 ? "1px solid" : "none"}
              borderColor="gray.100"
              onMouseDown={(e) => { e.preventDefault(); doSearch(query); }}
              onMouseEnter={() => setActiveIdx(-1)}
            >
              <Icon as={FiSearch} boxSize={4} color={RED} flexShrink={0} />
              <Text noOfLines={1}>
                {t("search_direct")}{" "}
                <Box as="span" fontWeight="800" color={RED}>«{query.trim()}»</Box>
              </Text>
            </ListItem>

            {/* Suggestions */}
            {suggestions.map((s, i) => (
              <ListItem
                key={s}
                px={4} py={2.5} cursor="pointer" minH="44px"
                bg={i === activeIdx ? "gray.50" : "white"}
                color="gray.800"
                fontSize="md"
                fontWeight={i === activeIdx ? "600" : "400"}
                _hover={{ bg: "gray.50" }}
                transition="background 0.1s"
                display="flex" alignItems="center" gap={2}
                onMouseDown={(e) => { e.preventDefault(); setQuery(s); doSearch(s); }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <Icon as={FaSearch} boxSize={3.5} color="gray.400" flexShrink={0} />
                <Text noOfLines={1} fontSize="sm">{s}</Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
export default function Navbar({ auth, setAuth }) {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isAdmin, setIsAdmin] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [staticModal, setStaticModal] = useState(false);
  const toggleShow = () => setStaticModal(!staticModal);
  const { t, lang, setLang } = useLang();
  const { unreadCount } = useChatNotif();
  const { ids: favIds } = useFavorites();

  useEffect(() => {
    const checkAdmin = async () => {
      if (auth) {
        try {
          const authToken = localStorage.getItem("authToken");
          const res = await axios.get(`${backendUrl}/admin/check`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          setIsAdmin(res.data.isAdmin);
        } catch {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  function handleLogout() {
    // La liste des clés vivait ici ET dans App.js, où elle était incomplète.
    // Un seul endroit efface désormais l'identité (utils/authEvents.js).
    clearIdentity();
    navigate("/");
    setAuth(false);
  }

  const handleSearch = useCallback(
    (q) => {
      navigate(`/results?query=${encodeURIComponent(q)}`);
    },
    [navigate]
  );

  // Identité de l'utilisateur : lue en state, pas directement dans localStorage.
  // Lire au rendu affichait l'ancien nom/photo après une modification de profil,
  // car React ne re-rend pas quand localStorage change.
  const [identity, setIdentity] = useState(readIdentity);

  useEffect(() => {
    const refresh = () => setIdentity(readIdentity());
    // `storage` couvre les autres onglets, l'événement maison le nôtre
    window.addEventListener("storage", refresh);
    window.addEventListener(AUTH_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(AUTH_CHANGED_EVENT, refresh);
    };
  }, []);

  // Se resynchronise aussi à la connexion / déconnexion
  useEffect(() => setIdentity(readIdentity()), [auth]);

  const { name, picture } = identity;

  return (
    <Box position="sticky" top="0" zIndex="1000" bg="white" boxShadow="0 2px 8px rgba(0,0,0,0.10)">

      {/* ── Main row ── */}
      <Container maxW="container.xl" py={2}>
        <Flex align="center" gap={{ base: 2, md: 3 }}>
          {/* Logo */}
          <Box as={Link} to="/" flexShrink={0} display="flex" alignItems="center" _hover={{ opacity: 0.85 }} transition="opacity 0.2s">
            <Box as="img" src="/logo.png" alt="9achna" h={{ base: "32px", md: "40px" }} width="auto" loading="eager" fetchPriority="low" />
          </Box>

          {/* Search bar — desktop only */}
          <Box flex="1" display={{ base: "none", md: "block" }}>
            <SearchWithAutocomplete onSearch={handleSearch} placeholder={t("search_placeholder")} />
          </Box>

          {/* Right actions */}
          <HStack spacing={{ base: 1, md: 1.5 }} flexShrink={0} ml={{ base: "auto", md: 0 }}>

            {/* Language toggle — desktop */}
            <HStack spacing={0} bg="gray.100" borderRadius="full" p="2px" border="1px solid" borderColor="gray.200" display={{ base: "none", md: "flex" }}>
              {["fr", "ar"].map((l) => (
                <Button key={l} size="sm" onClick={() => setLang(l)}
                  bg={lang === l ? RED : "transparent"} color={lang === l ? "white" : "gray.600"}
                  fontWeight="700" fontSize="sm" _hover={{ bg: lang === l ? RED_DARK : "gray.200" }}
                  borderRadius="full" minW="40px" h="36px" px={3} transition="all 0.2s"
                  fontFamily={l === "ar" ? "Cairo, sans-serif" : undefined}
                >
                  {l === "fr" ? "FR" : "ع"}
                </Button>
              ))}
            </HStack>

            {/* Favorites */}
            {auth && (
              <Box position="relative">
                <IconButton as={Link} to="/favorites" icon={<FaHeart size="20" />} variant="ghost"
                  color={favIds.size > 0 ? RED : "gray.600"} _hover={{ bg: "red.50", color: RED }}
                  borderRadius="md" size="lg" minW="44px" minH="44px" aria-label="Favoris" />
                {favIds.size > 0 && (
                  <Badge position="absolute" top="-1px" right="-1px" bg={RED} color="white"
                    borderRadius="full" fontSize="9px" minW="16px" h="16px"
                    display="flex" alignItems="center" justifyContent="center"
                    fontWeight="800" border="2px solid white" pointerEvents="none">
                    {favIds.size > 99 ? "99+" : favIds.size}
                  </Badge>
                )}
              </Box>
            )}

            {/* Chat */}
            {auth && (
              <Box position="relative">
                <IconButton as={Link} to="/chat"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z"/>
                    </svg>
                  }
                  variant="ghost" color={RED} _hover={{ bg: "red.50" }} borderRadius="md" size="lg" minW="44px" minH="44px" aria-label={t("messages")} />
                {unreadCount > 0 && (
                  <Badge position="absolute" top="-1px" right="-1px" bg={RED} color="white"
                    borderRadius="full" fontSize="9px" minW="16px" h="16px"
                    display="flex" alignItems="center" justifyContent="center"
                    fontWeight="800" border="2px solid white" pointerEvents="none">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </Badge>
                )}
              </Box>
            )}

            {/* Account menu — desktop */}
            {auth ? (
              <Menu>
                <MenuButton display={{ base: "none", md: "block" }}>
                  <HStack spacing={2} px={2} minH="44px" borderRadius="md" _hover={{ bg: "gray.100" }} cursor="pointer" transition="background 0.2s" role="button" aria-haspopup="menu">
                    <Avatar {...getAvatarProps(picture, 36)} size="sm" />
                    <Text color="gray.800" fontSize="md" fontWeight="600" display={{ base: "none", lg: "block" }}>{name?.split(" ")[0]}</Text>
                  </HStack>
                </MenuButton>
                <MenuList p={2} borderRadius="xl" boxShadow="xl" border="1px solid" borderColor="gray.100" minW="210px" mt={2}>
                  <Box px={3} py={2} mb={1}>
                    <Text fontWeight="700" fontSize="md" color="gray.800" noOfLines={1}>{name}</Text>
                    <Badge bg={isAdmin ? "purple.100" : "red.100"} color={isAdmin ? "purple.700" : RED} borderRadius="full" fontSize="sm">
                      {isAdmin ? t("admin") : t("member")}
                    </Badge>
                  </Box>
                  <Divider mb={1} />
                  <MenuItem borderRadius="md" py={3} minH="44px" icon={<Icon as={FaUser} color="gray.600" boxSize={4} />} onClick={() => navigate("/editprofile")} _hover={{ bg: "gray.50" }} fontSize="md">{t("profile")}</MenuItem>
                  <MenuItem borderRadius="md" py={3} minH="44px" icon={<Icon as={FaHeart} color="red.500" boxSize={4} />} onClick={() => navigate("/myads")} _hover={{ bg: "gray.50" }} fontSize="md">{t("my_ads")}</MenuItem>
                  {isAdmin && (
                    <MenuItem borderRadius="md" py={3} minH="44px" icon={<Icon as={FaShieldAlt} color="purple.500" boxSize={4} />} onClick={() => navigate("/admin")} _hover={{ bg: "gray.50" }} fontSize="md">{t("admin")}</MenuItem>
                  )}
                  <Divider my={1} />
                  <MenuItem borderRadius="md" py={3} minH="44px" icon={<Icon as={FaSignOutAlt} color="red.500" boxSize={4} />} onClick={handleLogout} color="red.500" _hover={{ bg: "red.50" }} fontSize="md">{t("logout")}</MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <Button variant="ghost" color={RED} _hover={{ bg: "red.50" }} onClick={toggleShow} size="md" minH="44px" fontWeight="600" fontSize="md" display={{ base: "none", md: "flex" }}>
                {t("sign_in")}
              </Button>
            )}

            {/* Sell CTA — desktop */}
            <Button
              onClick={() => auth ? navigate("/sell") : toggleShow()}
              leftIcon={<FaPlus />} bg={RED} color="white" borderRadius="md"
              fontWeight="700" size="md" minH="44px" fontSize="md" px={{ base: 4, md: 5 }}
              _hover={{ bg: RED_DARK, transform: "translateY(-1px)" }} _active={{ transform: "translateY(0)" }}
              transition="all 0.2s" display={{ base: "none", sm: "flex" }} flexShrink={0}>
              <Box display={{ base: "none", lg: "block" }}>{t("publish_ad")}</Box>
              <Box display={{ base: "block", lg: "none" }}>+</Box>
            </Button>

            {/* Mobile hamburger */}
            <IconButton icon={<HamburgerIcon boxSize={6} />} variant="ghost" color={RED}
              _hover={{ bg: "red.50" }} display={{ base: "flex", md: "none" }}
              onClick={onOpen} aria-label="Ouvrir le menu" size="lg" minW="44px" minH="44px" />
          </HStack>
        </Flex>
      </Container>

      {/* ── Mobile search row ── */}
      <Box display={{ base: "block", md: "none" }} px={3} pb={2} borderTop="1px solid" borderColor="gray.100">
        <SearchWithAutocomplete onSearch={handleSearch} placeholder={t("search_placeholder")} />
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader bg={RED} color="white" fontSize="md" fontWeight="700">
            9achna
          </DrawerHeader>
          <DrawerBody px={4} py={5}>
            <VStack spacing={3} align="stretch">
              {/* Mobile search with autocomplete */}
              <SearchWithAutocomplete
                onSearch={(q) => { handleSearch(q); onClose(); }}
                placeholder={t("search_placeholder")}
                inputProps={{ borderRadius: "md", fontSize: "sm" }}
              />

              <Divider />

              <Button as={Link} to="/" variant="ghost" justifyContent="flex-start" onClick={onClose} color="gray.700" fontWeight="600" size="md" minH="44px" fontSize="md">
                {t("home")}
              </Button>

              {auth ? (
                <>
                  <Button as={Link} to="/chat" variant="ghost" justifyContent="flex-start"
                    leftIcon={
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z"/>
                      </svg>
                    }
                    onClick={onClose} color="gray.700" size="md" minH="44px" fontSize="md">{t("messages")}</Button>
                  <Button as={Link} to="/favorites" variant="ghost" justifyContent="flex-start" leftIcon={<FaHeart />} onClick={onClose} color={RED} size="md" minH="44px" fontSize="md">Favoris {favIds.size > 0 && `(${favIds.size})`}</Button>
                  <Button as={Link} to="/myads" variant="ghost" justifyContent="flex-start" leftIcon={<FaHeart />} onClick={onClose} color="gray.700" size="md" minH="44px" fontSize="md">{t("my_ads")}</Button>
                  <Button as={Link} to="/editprofile" variant="ghost" justifyContent="flex-start" leftIcon={<FaUser />} onClick={onClose} color="gray.700" size="md" minH="44px" fontSize="md">{t("profile")}</Button>
                  <Divider />
                  <Button onClick={handleLogout} variant="ghost" justifyContent="flex-start" leftIcon={<FaSignOutAlt />} color="red.500" size="md" minH="44px" fontSize="md">{t("logout")}</Button>
                </>
              ) : (
                <Button onClick={() => { toggleShow(); onClose(); }} bg={RED} color="white" borderRadius="md" _hover={{ bg: RED_DARK }} fontWeight="700" size="md" minH="44px" fontSize="md">
                  {t("sign_in")}
                </Button>
              )}

              <Button leftIcon={<FaPlus />} bg={RED} color="white" borderRadius="md" _hover={{ bg: RED_DARK }} fontWeight="700" size="md" minH="44px" fontSize="md"
                onClick={() => { onClose(); auth ? navigate("/sell") : toggleShow(); }}>
                {t("publish_ad")}
              </Button>

              {/* Language toggle in drawer */}
              <Divider />
              <HStack justify="center" spacing={2}>
                <Button
                  size="md"
                  minH="44px"
                  fontSize="md"
                  onClick={() => setLang("fr")}
                  bg={lang === "fr" ? RED : "gray.100"}
                  color={lang === "fr" ? "white" : "gray.700"}
                  borderRadius="full"
                  fontWeight="700"
                  _hover={{ bg: lang === "fr" ? RED_DARK : "gray.200" }}
                  flex="1"
                >
                  Français
                </Button>
                <Button
                  size="md"
                  minH="44px"
                  fontSize="md"
                  onClick={() => setLang("ar")}
                  bg={lang === "ar" ? RED : "gray.100"}
                  color={lang === "ar" ? "white" : "gray.700"}
                  borderRadius="full"
                  fontWeight="700"
                  _hover={{ bg: lang === "ar" ? RED_DARK : "gray.200" }}
                  flex="1"
                  fontFamily="Cairo, sans-serif"
                >
                  العربية
                </Button>
              </HStack>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Modallogin
        setStaticModal={setStaticModal}
        toggleShow={toggleShow}
        staticModal={staticModal}
      />
    </Box>
  );
}
